import { CAPTION_TEXT_FORMAT } from '@vbee-holding/aivoice-shared-lib';
import { FONT_STYLE } from '@src/constants/dubbing';

// One entry per face, keyed family:weight:url. Holds the promise, not the
// result, so parallel callers join the request already in flight.
const faces = new Map();

// Slant has one representation: the ITALIC flag the chip writes, the renderer
// reads and the API resolves from.
const isItalic = (formats) =>
  Boolean(formats?.includes(CAPTION_TEXT_FORMAT.ITALIC));

/**
 * The url of the face a caption style names, or null without an exact match.
 * Anything looser belongs to the API — it falls back by nearest weight and then
 * by family, and guessing differently here would draw one face in the preview
 * and burn another into the export. Slant comes from the `ITALIC` flag in
 * `formats`, the one representation the renderer and the API both read.
 */
const findFaceUrl = (fonts, { fontFamily, fontWeight, formats } = {}) => {
  const wanted = isItalic(formats) ? FONT_STYLE.ITALIC : FONT_STYLE.NORMAL;
  const face = fonts
    ?.find(({ family }) => family === fontFamily)
    ?.variants.find(
      ({ weight, style }) => weight === fontWeight && style === wanted,
    );
  return face?.url ?? null;
};

// `text.font.url` only refreshes on a save, so it still points at the upright
// face right after italic is turned on. Better no url than the wrong slant.
const resolveFaceUrl = (fonts, text) =>
  findFaceUrl(fonts, text) ??
  (isItalic(text?.formats) ? null : text?.font?.url);

/**
 * Makes a face usable by the canvas, once per face. The renderer only sets
 * `ctx.font` and never loads one, and an unregistered face turns Vietnamese
 * marks to tofu.
 *
 * The descriptors have to match what `ctx.font` will ask for — family, weight
 * and slant. Registering an italic file as a normal face is worse than not
 * registering it: asked for italic, the canvas finds no italic face and slants
 * the already-slanted one again.
 */
const loadCaptionFont = ({ fontFamily, fontWeight, formats } = {}, fontUrl) => {
  // Nothing to fetch: wait for the app fonts the style falls back to.
  if (!fontFamily || !fontUrl)
    return document.fonts?.ready ?? Promise.resolve();

  const style = isItalic(formats) ? 'italic' : 'normal';
  const key = `${fontFamily}:${fontWeight}:${style}:${fontUrl}`;
  const cached = faces.get(key);
  if (cached) return cached;

  const pending = new FontFace(fontFamily, `url(${fontUrl})`, {
    weight: String(fontWeight),
    style,
  })
    .load()
    .then((face) => {
      document.fonts.add(face);
    })
    // Drawing with the fallback beats drawing nothing. Drop the entry to retry.
    .catch(() => {
      faces.delete(key);
    });

  faces.set(key, pending);
  return pending;
};

export { findFaceUrl, resolveFaceUrl, loadCaptionFont };
